// =====================================================================
// Textes légaux
// =====================================================================
// Écrits pour être lus, pas pour se protéger. Un utilisateur de Kinshasa
// ou de Bruxelles doit comprendre en deux minutes ce que Yambi fait de
// ses données et ce qu'il risque — sinon le consentement n'en est pas un.
//
// Ces textes décrivent le fonctionnement réel de l'application. Si le
// code change, ils changent. Faites-les relire par un juriste avant une
// mise en service réelle : ils sont honnêtes, ils ne sont pas validés.
// =====================================================================

/**
 * Adresse de contact, fixée à la construction. Renvoyer l'utilisateur
 * vers « l'adresse indiquée ailleurs » ne vaut rien : une politique de
 * confidentialité doit donner le moyen d'exercer ses droits, pas la
 * promesse qu'il existe quelque part.
 */
const CONTACT = import.meta.env.VITE_CONTACT ?? 'contact@yambi.cd';

export function Texte({
  quoi,
  onRetour,
}: {
  quoi: 'confidentialite' | 'conditions';
  onRetour: () => void;
}) {
  return (
    <>
      <button className="bouton discret retour" onClick={onRetour}>
        ← Retour
      </button>
      {quoi === 'confidentialite' ? <Confidentialite /> : <Conditions />}
    </>
  );
}

function Confidentialite() {
  return (
    <div className="texte-legal">
      <h2 className="titre-page">Politique de confidentialité</h2>

      <h3>Ce que Yambi conserve</h3>
      <p>
        Votre adresse e-mail et votre mot de passe, sous forme chiffrée. Votre
        nom, votre sexe, votre village d'origine, et votre date de naissance si
        vous la renseignez. Les personnes et les liens de parenté que vous
        déclarez. Les messages que vous échangez lors d'un rapprochement.
      </p>
      <p>
        Votre numéro de téléphone est facultatif. Il ne sert jamais à vous
        connecter, seulement à ce qu'un proche vous retrouve s'il ne connaît
        que votre numéro.
      </p>

      <h3>Qui peut voir quoi</h3>
      <p>
        <strong>Il n'y a aucune recherche dans Yambi.</strong> Personne ne peut
        chercher votre nom, parcourir un annuaire ou tomber sur votre famille.
      </p>
      <p>
        Vous n'êtes visible que par les personnes avec qui un lien de parenté a
        été établi, et seulement jusqu'à cinq liens de distance — environ
        jusqu'aux enfants de vos cousins germains. Cette règle est appliquée
        par la base de données elle-même, et non par le code de l'application :
        une erreur de programmation ne peut pas l'outrepasser.
      </p>
      <p>
        Lors d'un rapprochement, votre identité reste <strong>masquée</strong>{' '}
        tant que les deux personnes n'ont pas confirmé. Ni votre nom, ni votre
        village, ni celui de l'ancêtre concerné ne sont révélés avant.
      </p>

      <h3>Ce que nous ne faisons pas</h3>
      <p>
        Nous ne vendons aucune donnée. Nous ne faisons pas de publicité. Nous
        n'utilisons aucun traceur publicitaire. Il n'existe aucun moyen, pour
        qui que ce soit, d'obtenir une liste de personnes par nom, par village
        ou par appartenance familiale.
      </p>
      <p>
        Ce dernier point n'est pas une politesse : un registre consultable qui
        relierait nom, clan et localisation serait dangereux. L'application est
        conçue pour qu'il ne puisse pas exister.
      </p>

      <h3>Vos droits</h3>
      <p>
        Depuis <em>Mon compte</em>, vous pouvez à tout moment télécharger
        l'intégralité de vos données, et supprimer votre compte.
      </p>
      <p>
        La suppression efface votre compte, votre adresse et vos messages. Votre
        fiche, elle, est <strong>rendue anonyme</strong> plutôt que détruite si
        des proches y sont rattachés : la supprimer couperait leur lignée, qui
        est leur donnée à eux. Si personne ne dépend de vous, votre fiche est
        entièrement supprimée.
      </p>

      <h3>Les personnes que vous déclarez</h3>
      <p>
        Quand vous ajoutez un proche, vous enregistrez des informations qui le
        concernent. Elles ne sont visibles que de votre parenté proche. Si cette
        personne a un compte, elle doit confirmer le lien avant que quoi que ce
        soit soit partagé, et elle peut refuser — définitivement.
      </p>

      <h3>Conservation et sécurité</h3>
      <p>
        Les données sont conservées tant que votre compte existe. Les mots de
        passe sont hachés, jamais stockés en clair. Les liens envoyés par
        courriel ne sont pas conservés : seule leur empreinte l'est, de sorte
        qu'une fuite de la base ne donnerait aucun lien utilisable.
      </p>

      <h3>Nous écrire</h3>
      <p>
        Pour toute question sur vos données, ou pour exercer vos droits,
        écrivez à <a href={`mailto:${CONTACT}`}>{CONTACT}</a>. Nous répondons
        dans un délai d'un mois.
      </p>
      <p>
        Vous pouvez aussi saisir l'autorité de protection des données de votre
        pays de résidence.
      </p>
    </div>
  );
}

function Conditions() {
  return (
    <div className="texte-legal">
      <h2 className="titre-page">Conditions d'utilisation</h2>

      <h3>Ce qu'est Yambi</h3>
      <p>
        Une plateforme qui vous aide à construire votre arbre généalogique et
        à retrouver des parents que vous ne connaissiez pas. Elle est fondée
        sur ce que ses utilisateurs déclarent.
      </p>

      <h3>Ce que Yambi n'est pas</h3>
      <p>
        <strong>Yambi n'est pas une preuve de filiation.</strong> Les
        informations proviennent de la mémoire des familles, pas de l'état
        civil. Elles peuvent être incomplètes ou erronées. Elles ne valent pas
        acte, et ne sauraient être opposées dans une succession, un litige
        foncier ou une procédure administrative.
      </p>

      <h3>Vos engagements</h3>
      <ul>
        <li>Déclarer de bonne foi ce que vous croyez exact.</li>
        <li>
          Ne pas vous faire passer pour un parent que vous n'êtes pas. Le
          rapprochement repose sur la confiance mutuelle ; en abuser pour
          approcher une famille, obtenir de l'argent ou des informations est
          un détournement de la plateforme.
        </li>
        <li>
          Respecter les personnes que vous rencontrez. Un refus est définitif
          et ne peut pas être relancé.
        </li>
        <li>
          Ne pas utiliser Yambi pour constituer des listes de personnes par
          origine, appartenance familiale ou ethnique.
        </li>
      </ul>

      <h3>Ce que nous pouvons faire</h3>
      <p>
        Suspendre un compte qui enfreint ces règles, en particulier en cas
        d'usurpation d'identité ou de harcèlement. Corriger ou retirer une
        information manifestement fausse qui nous est signalée.
      </p>

      <h3>Disponibilité</h3>
      <p>
        La plateforme est fournie en l'état. Nous faisons de notre mieux pour
        qu'elle reste disponible et pour que vos données soient sauvegardées,
        sans pouvoir le garantir. Exportez vos données de temps en temps : c'est
        votre meilleure assurance.
      </p>

      <h3>Désaccords dans une famille</h3>
      <p>
        Deux branches peuvent se contredire sur un aïeul. Yambi n'arbitre pas
        les questions de filiation : c'est aux familles de le faire. Nous ne
        trancherons pas à leur place.
      </p>
    </div>
  );
}
